import { I18nextProvider, i18nConfig, useLanguage } from '@cobliteam/i18n';
import { PermissionsProvider } from '@cobliteam/permissions';
import { Environment } from '@cobliteam/react-environments';
import Alert, { AlertContainer } from '@cobliteam/ui/components/Alert';
import { TooltipProvider } from '@cobliteam/ui/components/Tooltip';
import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { APIProvider } from '@vis.gl/react-google-maps';
import { ConnectedRouter } from 'connected-react-router';
import { basicLogger, withLDProvider } from 'launchdarkly-react-client-sdk';
import { NuqsAdapter } from 'nuqs/adapters/react';
import { Provider as ReduxProvider } from 'react-redux';
import Router from './Router';
import { history, store } from './store';

const env = Environment.getInstance().getEnvironment();

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnMount: false, // Never change data on mount to true - Loop Fetch on map.
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
      retry: 3,
      retryDelay: 1000,
      staleTime: 1000 * 60 * 5,
    },
  },
  mutationCache: new MutationCache({
    onSuccess: (_data, _variables, _context, mutation) => {
      if (mutation.meta?.successMessage) {
        Alert.success((mutation.meta?.successMessage as string) || 'Sucesso ao carregar dados');
      }
    },
    onError: (_data, _variables, _context, mutation) => {
      if (mutation.meta?.errorMessage) {
        Alert.error((mutation.meta?.errorMessage as string) || 'Erro ao carregar dados');
      }
    },
  }),
  queryCache: new QueryCache({
    onSuccess: (_data, query) => {
      if (query.meta?.successMessage) {
        Alert.success((query.meta?.successMessage as string) || 'Sucesso ao carregar dados');
      }
    },
    onError: (_data, query) => {
      if (query.meta?.errorMessage) {
        Alert.error((query.meta?.errorMessage as string) || 'Erro ao carregar dados');
      }
    },
  }),
});

const Dashboard = () => {
  const { googleMapsLanguage } = useLanguage();

  return (
    <QueryClientProvider client={queryClient}>
      <ReduxProvider store={store}>
        <APIProvider apiKey={env.googleMapsVectorKey} libraries={['places']} language={googleMapsLanguage}>
          <I18nextProvider i18n={i18nConfig}>
            <TooltipProvider delayDuration={300}>
              <PermissionsProvider>
                <ConnectedRouter history={history}>
                  <NuqsAdapter>
                    <Router />
                    <AlertContainer richColors position='top-right' pauseWhenPageIsHidden />
                  </NuqsAdapter>
                </ConnectedRouter>
              </PermissionsProvider>
            </TooltipProvider>
          </I18nextProvider>
        </APIProvider>
      </ReduxProvider>
    </QueryClientProvider>
  );
};

export default withLDProvider({
  clientSideID: env.launchDarklyId,
  options: {
    logger: basicLogger({ level: 'none' }),
  },
})(Dashboard);
